import { useState, useEffect, useCallback, type Dispatch, type SetStateAction } from 'react';
import {
  BookOpen,
  Plus,
  Save,
  X,
  Loader2,
  RotateCcw,
  AlertTriangle,
  Truck,
  CreditCard,
  Info,
  Package,
  ChevronDown,
  Copy,
  Check,
  Trash2,
  Store,
  Globe,
  Sparkles,
  FlaskConical,
  Upload,
  FileText,
} from 'lucide-react';
import { knowledgeBaseApi, messagesApi, storeApi, warehouseApi, type Product } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { Select } from '../components/ui/Select';
import { useBackdropClickClose } from '../hooks/useBackdropClickClose';

// ============================================
// Types
// ============================================

interface StoreItem {
  id: string;
  name: string;
  url: string;
  isActive: boolean;
}

interface SectionFieldDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'textarea' | 'string_list' | 'select' | 'multi_checkbox' | 'repeatable_carriers' | 'repeatable_faq';
  placeholder?: string;
  options?: { value: string; label: string }[];
  allowCustom?: boolean;
}

interface SectionDefinition {
  sectionType: string;
  title: string;
  icon: string;
  color: string;
  fields: SectionFieldDef[];
}

interface KnowledgeBaseItem {
  id: string;
  title: string;
  content: string;
  storeId: string | null;
  category: string | null;
  tags: string[];
  isActive: boolean;
  position: number;
  sectionType: string | null;
  configData: Record<string, unknown> | null;
  isPreset: boolean;
  productSku: string | null;
  createdAt: string;
  updatedAt: string;
}

interface LabProductWithKb {
  id: string;
  name: string;
  imageUrl: string | null;
  status: string;
  knowledgeBases: {
    id: string;
    name: string;
    condensedSummary: string | null;
    updatedAt: string;
  }[];
}

interface SalesProductOption {
  id: string;
  name: string;
  sku: string | null;
  storeId: string | null;
  woocommerceProductId: string | null;
}

interface ProductFaqBundleOption {
  id: string;
  name: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  productIds: number[];
  packages: Array<{
    id: string;
    title: string;
    qty: number;
    price: number;
    promoPrice: number | null;
    message1: string | null;
    message2: string | null;
    bonuses: unknown;
  }>;
}

interface ProductFaqPriceVariant {
  id: string;
  name: string;
  quantity: number;
  regularPrice?: number;
  salePrice?: number;
  currency: string;
  note?: string;
}

const PRODUCT_FAQ_BUNDLE_STATUS_LABELS: Record<ProductFaqBundleOption['status'], string> = {
  ACTIVE: 'aktywny',
  DRAFT: 'szkic',
  PAUSED: 'wstrzymany',
  ARCHIVED: 'zarchiwizowany',
};

interface KnowledgeImportProposal {
  id: string;
  sectionType: string;
  fieldKey: string;
  value: unknown;
  currentValue: unknown;
  sourceExcerpt: string;
  confidence: number;
  conflict: boolean;
  defaultSelected: boolean;
  productName?: string;
  productSku?: string;
}

interface KnowledgeImportRecord {
  id: string;
  storeId: string;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'APPLIED' | 'FAILED' | 'CANCELLED';
  progress: number;
  proposals: KnowledgeImportProposal[];
  error: string | null;
}

interface MessageTemplateOption {
  id: string;
  name: string;
  bodyHtml: string;
  bodyText: string | null;
  storeId: string | null;
  isActive: boolean;
  store?: { id: string; name: string } | null;
}

interface KnowledgeTemplateBinding {
  global?: string;
  stores?: Record<string, string>;
}

type KnowledgeTemplateBindings = Record<string, KnowledgeTemplateBinding>;

type NewFaqMode = 'manual' | 'lab' | 'import';

const TEMPLATE_BINDING_FIELD_KEYS = new Set([
  'returnConditions',
  'returnProcess',
  'uncollectedParcelResendInstructions',
  'exchangePolicy',
  'complaintProcess',
  'requiredInfo',
  'additionalInfo',
  'refundProcess',
  'additionalPolicies',
]);

const PRODUCT_FAQ_CUSTOM_FIELD_KEYS = new Set([
  'productName',
  'regularPrice',
  'salePrice',
  'currency',
  'unitsPerSet',
]);

const DEFAULT_NEW_FAQ_DATA: Record<string, unknown> = {
  productName: '',
  warehouseProductId: '',
  woocommerceProductId: '',
  storeId: '',
  productSku: '',
  skuPrefix: '',
  includedWooCommerceProductIds: [],
  excludedWooCommerceProductIds: [],
  pricingMode: 'manual',
  bundleId: '',
  priceVariants: [],
  labProductId: '',
  keyFeatures: '',
  usage: '',
  warnings: '',
  faq: [],
};

function createPriceVariantId(): string {
  return globalThis.crypto?.randomUUID?.() || `price-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function skuMatchesFamilyPrefix(sku: string, prefix: string): boolean {
  const normalizedSku = sku.trim().toLocaleLowerCase('pl-PL');
  const normalizedPrefix = prefix.trim().toLocaleLowerCase('pl-PL').replace(/[._\-/]+$/, '');
  if (!normalizedSku || !normalizedPrefix) return false;
  if (normalizedSku === normalizedPrefix) return true;
  return ['_', '-', '.', '/'].some(separator => normalizedSku.startsWith(`${normalizedPrefix}${separator}`));
}

function normalizeProductFaqFormData(
  configData: Record<string, unknown>,
  storeId: string,
): Record<string, unknown> {
  const hasPriceVariants = Array.isArray(configData.priceVariants) && configData.priceVariants.length > 0;
  const legacyPriceVariant = !hasPriceVariants && (
    typeof configData.regularPrice === 'number'
    || typeof configData.salePrice === 'number'
  ) ? [{
    id: createPriceVariantId(),
    name: Number(configData.unitsPerSet || 1) === 1 ? '1 sztuka' : `${Number(configData.unitsPerSet)} szt.`,
    quantity: Number(configData.unitsPerSet || 1),
    regularPrice: typeof configData.regularPrice === 'number' ? configData.regularPrice : undefined,
    salePrice: typeof configData.salePrice === 'number' ? configData.salePrice : undefined,
    currency: typeof configData.currency === 'string' && configData.currency ? configData.currency : 'PLN',
  }] : [];

  const normalized: Record<string, unknown> = {
    ...configData,
    storeId: configData.storeId || storeId,
    skuPrefix: configData.skuPrefix || configData.productSku || '',
    includedWooCommerceProductIds: Array.isArray(configData.includedWooCommerceProductIds)
      ? configData.includedWooCommerceProductIds
      : [],
    excludedWooCommerceProductIds: Array.isArray(configData.excludedWooCommerceProductIds)
      ? configData.excludedWooCommerceProductIds
      : [],
    pricingMode: configData.pricingMode === 'bundle' ? 'bundle' : 'manual',
    bundleId: typeof configData.bundleId === 'string' ? configData.bundleId : '',
    priceVariants: hasPriceVariants ? configData.priceVariants : legacyPriceVariant,
  };
  if (legacyPriceVariant.length > 0) {
    delete normalized.regularPrice;
    delete normalized.salePrice;
    delete normalized.unitsPerSet;
  }
  return normalized;
}

function productFaqValidationError(data: Record<string, unknown>): string | null {
  if (!data.warehouseProductId) return 'Wybierz produkt bazowy z logistyki';
  if (data.pricingMode === 'bundle' && !data.bundleId) return 'Wybierz bundle, z którego AI ma pobierać ceny';
  const priceVariants = Array.isArray(data.priceVariants) ? data.priceVariants as ProductFaqPriceVariant[] : [];
  if (data.pricingMode !== 'bundle' && priceVariants.some(variant => (
    !variant.name.trim()
    || !variant.currency.trim()
    || !Number.isInteger(variant.quantity)
    || variant.quantity < 1
    || (variant.regularPrice === undefined && variant.salePrice === undefined)
  ))) {
    return 'Każdy wariant cenowy musi mieć nazwę, ilość, walutę i co najmniej jedną cenę';
  }
  return null;
}

function withSelectedBaseProduct(
  current: Record<string, unknown>,
  productId: string,
  products: SalesProductOption[],
): Record<string, unknown> {
  const product = products.find(option => option.id === productId);
  const previousSku = typeof current.productSku === 'string' ? current.productSku : '';
  const currentPrefix = typeof current.skuPrefix === 'string' ? current.skuPrefix : '';
  const shouldReplacePrefix = !currentPrefix || currentPrefix === previousSku;
  return {
    ...current,
    warehouseProductId: productId,
    woocommerceProductId: product?.woocommerceProductId || '',
    storeId: product?.storeId || current.storeId || '',
    productSku: product?.sku || '',
    skuPrefix: shouldReplacePrefix ? product?.sku || '' : currentPrefix,
    ...(product ? { productName: product.name } : {}),
  };
}

function formatKnowledgeBaseDateTime(value?: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('pl-PL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================
// Icon map
// ============================================

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  RotateCcw,
  AlertTriangle,
  Truck,
  CreditCard,
  Info,
  Package,
};

const COLOR_MAP: Record<string, string> = {
  orange: 'from-warning-bg to-warning-bg border-warning-border text-warning',
  red: 'from-danger-bg to-danger-bg border-danger-border text-danger',
  blue: 'from-info-bg to-info-bg border-info-border text-info',
  green: 'from-success-bg to-success-bg border-success-border text-success',
  cyan: 'from-info-bg to-info-bg border-info-border text-info',
  purple: 'from-info-bg to-info-bg border-info-border text-info',
};

// ============================================
// Constants for carrier selects
// ============================================

const CARRIER_NAME_OPTIONS = [
  { value: 'InPost Paczkomaty', label: 'InPost Paczkomaty' },
  { value: 'InPost Kurier', label: 'InPost Kurier' },
  { value: 'DPD', label: 'DPD' },
  { value: 'DHL', label: 'DHL' },
  { value: 'Poczta Polska', label: 'Poczta Polska' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'UPS', label: 'UPS' },
  { value: 'GLS', label: 'GLS' },
  { value: 'Orlen Paczka', label: 'Orlen Paczka' },
];

const DELIVERY_TIME_OPTIONS = [
  { value: '1 dzień roboczy', label: '1 dzień roboczy' },
  { value: '1-2 dni robocze', label: '1-2 dni robocze' },
  { value: '2-3 dni robocze', label: '2-3 dni robocze' },
  { value: '3-5 dni roboczych', label: '3-5 dni roboczych' },
  { value: '5-7 dni roboczych', label: '5-7 dni roboczych' },
];

// ============================================
// Helpers
// ============================================

function countFilledFields(configData: Record<string, unknown> | null): number {
  if (!configData) return 0;
  let count = 0;
  for (const [key, value] of Object.entries(configData)) {
    if (key.startsWith('_')) continue;
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (typeof value === 'number' && isNaN(value)) continue;
    count++;
  }
  return count;
}

function countFilledProductFaqSections(configData: Record<string, unknown>): number {
  const hasPricing = configData.pricingMode === 'bundle'
    ? Boolean(configData.bundleId)
    : (Array.isArray(configData.priceVariants) && configData.priceVariants.length > 0)
      || typeof configData.regularPrice === 'number'
      || typeof configData.salePrice === 'number';
  return [
    Boolean(configData.warehouseProductId || configData.skuPrefix || configData.productSku),
    hasPricing,
    Boolean(configData.keyFeatures),
    Boolean(configData.usage),
    Boolean(configData.warnings),
    Array.isArray(configData.faq) && configData.faq.length > 0,
  ].filter(Boolean).length;
}

function generateProductPrompt(productName: string): string {
  return `Stwórz ustrukturyzowane FAQ obsługi klienta dla produktu "${productName}".

Uwzględnij sekcje:
1. Kluczowe cechy produktu
2. Zastosowanie / jak używać
3. Ostrzeżenia i przeciwwskazania
4. Najczęstsze pytania klientów (min. 5 pytań z odpowiedziami)

Format: każda sekcja z nagłówkiem, treść w punktach.
Język: polski.`;
}

function formatProposalValue(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

function parseProposalValue(originalValue: unknown, editedValue: string): unknown {
  if (typeof originalValue === 'number') {
    const parsed = Number(editedValue.replace(',', '.'));
    if (!Number.isFinite(parsed)) throw new Error('Wartość musi być liczbą.');
    return parsed;
  }
  if (Array.isArray(originalValue) || (originalValue && typeof originalValue === 'object')) {
    return JSON.parse(editedValue);
  }
  return editedValue;
}

function getTemplateBindings(configData: Record<string, unknown>): KnowledgeTemplateBindings {
  return (configData._templateBindings as KnowledgeTemplateBindings | undefined) || {};
}

function getTemplateBindingValue(configData: Record<string, unknown>, fieldKey: string, storeId?: string): string {
  const binding = getTemplateBindings(configData)[fieldKey];
  if (!binding) return '';
  return (storeId ? binding.stores?.[storeId] : undefined) || binding.global || '';
}

function setTemplateBindingValue(
  configData: Record<string, unknown>,
  fieldKey: string,
  templateId: string,
  storeId?: string,
): Record<string, unknown> {
  const bindings: KnowledgeTemplateBindings = { ...getTemplateBindings(configData) };
  const current = bindings[fieldKey] || {};
  const next: KnowledgeTemplateBinding = {
    ...(current.global ? { global: current.global } : {}),
    ...(current.stores ? { stores: { ...current.stores } } : {}),
  };

  if (storeId) {
    const stores = { ...(next.stores || {}) };
    if (templateId) stores[storeId] = templateId; else delete stores[storeId];
    if (Object.keys(stores).length > 0) next.stores = stores; else delete next.stores;
  } else if (templateId) {
    next.global = templateId;
  } else {
    delete next.global;
  }

  if (!next.global && !next.stores) delete bindings[fieldKey]; else bindings[fieldKey] = next;
  const result = { ...configData };
  if (Object.keys(bindings).length > 0) result._templateBindings = bindings; else delete result._templateBindings;
  return result;
}

function clearPerStoreTemplateBindings(configData: Record<string, unknown>, fieldKey: string): Record<string, unknown> {
  const bindings: KnowledgeTemplateBindings = { ...getTemplateBindings(configData) };
  const current = bindings[fieldKey];
  if (!current?.stores) return configData;
  if (current.global) bindings[fieldKey] = { global: current.global }; else delete bindings[fieldKey];
  const result = { ...configData };
  if (Object.keys(bindings).length > 0) result._templateBindings = bindings; else delete result._templateBindings;
  return result;
}

// ============================================
// Main component
// ============================================

export function KnowledgeBasePage() {
  const confirmDialog = useConfirmDialog();
  const [items, setItems] = useState<KnowledgeBaseItem[]>([]);
  const [stores, setStores] = useState<StoreItem[]>([]);
  const [definitions, setDefinitions] = useState<{ policies: SectionDefinition[]; productFaq: SectionDefinition } | null>(null);
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplateOption[]>([]);
  const [loading, setLoading] = useState(true);
  // Modal state
  const [activePanel, setActivePanel] = useState<{
    item: KnowledgeBaseItem;
    definition: SectionDefinition;
  } | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [copied, setCopied] = useState(false);

  // New product FAQ state
  const [showNewFaq, setShowNewFaq] = useState(false);
  const [newFaqMode, setNewFaqMode] = useState<NewFaqMode>('manual');
  const [newFaqData, setNewFaqData] = useState<Record<string, unknown>>({ ...DEFAULT_NEW_FAQ_DATA });
  const [savingNewFaq, setSavingNewFaq] = useState(false);

  // Lab generation state
  const [labProducts, setLabProducts] = useState<LabProductWithKb[]>([]);
  const [loadingLabProducts, setLoadingLabProducts] = useState(false);
  const [generatingFromLab, setGeneratingFromLab] = useState<string | null>(null);
  const [salesProducts, setSalesProducts] = useState<SalesProductOption[]>([]);
  const [loadingSalesProducts, setLoadingSalesProducts] = useState(false);
  const [productFaqBundles, setProductFaqBundles] = useState<ProductFaqBundleOption[]>([]);
  const [loadingProductFaqBundles, setLoadingProductFaqBundles] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [knowledgeImport, setKnowledgeImport] = useState<KnowledgeImportRecord | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [selectedImportProposalIds, setSelectedImportProposalIds] = useState<Set<string>>(new Set());
  const [importEdits, setImportEdits] = useState<Record<string, string>>({});

  const activePanelBackdropProps = useBackdropClickClose(() => setActivePanel(null), Boolean(activePanel));
  const newFaqBackdropProps = useBackdropClickClose(
    () => setShowNewFaq(false),
    Boolean(showNewFaq && !generatingFromLab),
  );

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Load stores + definitions
  useEffect(() => {
    Promise.all([
      storeApi.getAll(),
      knowledgeBaseApi.getSectionDefinitions(),
      messagesApi.getAllTemplates().catch(() => null),
    ]).then(([storesRes, defsRes, templatesRes]) => {
      const storeData = Array.isArray(storesRes.data) ? storesRes.data : (storesRes.data as any).data || [];
      setStores(storeData);
      setSelectedStoreId(current => current || storeData.find((store: StoreItem) => store.isActive)?.id || storeData[0]?.id || '');
      setDefinitions(defsRes.data.data);
      setMessageTemplates((templatesRes?.data?.data || []) as MessageTemplateOption[]);
    }).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      let res = await knowledgeBaseApi.getItems({ limit: 100 });
      const loadedItems = res.data.data as KnowledgeBaseItem[];
      const hasGlobalPolicies = loadedItems.some(item => item.isPreset && item.sectionType !== 'product_faq' && !item.storeId);
      if (!hasGlobalPolicies) {
        await knowledgeBaseApi.initPresets(undefined);
        res = await knowledgeBaseApi.getItems({ limit: 100 });
      }
      setItems(res.data.data);
    } catch {
      showToast('Błąd ładowania bazy wiedzy', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!knowledgeImport || !['PENDING', 'PROCESSING'].includes(knowledgeImport.status)) return;
    const timer = window.setInterval(async () => {
      try {
        const response = await knowledgeBaseApi.getImport(knowledgeImport.id);
        const next = response.data.data as KnowledgeImportRecord;
        setKnowledgeImport(next);
        if (next.status === 'READY') {
          setSelectedImportProposalIds(new Set(next.proposals.filter(proposal => proposal.defaultSelected).map(proposal => proposal.id)));
          setImportEdits(Object.fromEntries(next.proposals.map(proposal => [proposal.id, formatProposalValue(proposal.value)])));
        }
      } catch {
        window.clearInterval(timer);
        showToast('Nie udało się odświeżyć stanu importu', 'error');
      }
    }, 1500);
    return () => window.clearInterval(timer);
  }, [knowledgeImport?.id, knowledgeImport?.status]);

  // ESC closes modals
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (generatingFromLab) return;
        if (activePanel) setActivePanel(null);
        else if (showNewFaq) setShowNewFaq(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [activePanel, showNewFaq, generatingFromLab]);

  // ============================================
  // Handlers
  // ============================================

  const openPanel = (item: KnowledgeBaseItem) => {
    const def = definitions?.policies.find(d => d.sectionType === item.sectionType)
      || (item.sectionType === 'product_faq' ? definitions?.productFaq : null);
    if (!def) return;
    if (item.sectionType === 'product_faq' && item.storeId) setSelectedStoreId(item.storeId);
    const configData = (item.configData as Record<string, unknown>) || {};
    setFormData(item.sectionType === 'product_faq'
      ? normalizeProductFaqFormData(configData, item.storeId || '')
      : configData);
    setActivePanel({ item, definition: def });
    if (item.sectionType === 'product_faq' && item.storeId) {
      void loadSalesProducts(item.storeId);
      void loadProductFaqBundles(item.storeId);
    }
  };

  const savePanel = async () => {
    if (!activePanel) return;
    if (activePanel.item.sectionType === 'product_faq') {
      const validationError = productFaqValidationError(formData);
      if (validationError) {
        showToast(validationError, 'error');
        return;
      }
    }
    try {
      setSaving(true);
      await knowledgeBaseApi.updateItem(activePanel.item.id, {
        ...(activePanel.item.sectionType === 'product_faq' ? {
          title: `FAQ: ${formData.productName as string}`,
          storeId: (formData.storeId as string) || null,
        } : {}),
        configData: formData,
        productSku: (formData.productSku as string) || null,
      });
      showToast('Zapisano');
      setActivePanel(null);
      loadData();
    } catch {
      showToast('Błąd zapisu', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteProductFaq = async (item: KnowledgeBaseItem) => {
    if (!(await confirmDialog({
      title: 'Usuń FAQ',
      message: 'Czy na pewno chcesz usunąć to FAQ produktu?',
      confirmLabel: 'Usuń',
    }))) return;
    try {
      await knowledgeBaseApi.deleteItem(item.id);
      showToast('FAQ usunięte');
      loadData();
    } catch {
      showToast('Błąd usuwania', 'error');
    }
  };

  const createProductFaq = async () => {
    const validationError = productFaqValidationError(newFaqData);
    if (validationError) {
      showToast(validationError, 'error');
      return;
    }
    if (!newFaqData.productName) return;
    try {
      setSavingNewFaq(true);
      await knowledgeBaseApi.createItem({
        title: `FAQ: ${newFaqData.productName}`,
        content: '',
        storeId: (newFaqData.storeId as string) || undefined,
        sectionType: 'product_faq',
        configData: newFaqData,
        productSku: (newFaqData.productSku as string) || undefined,
        isPreset: true,
        isActive: true,
      });
      showToast('FAQ produktu dodane');
      setShowNewFaq(false);
      setNewFaqData({ ...DEFAULT_NEW_FAQ_DATA });
      loadData();
    } catch {
      showToast('Błąd tworzenia FAQ', 'error');
    } finally {
      setSavingNewFaq(false);
    }
  };

  const copyPrompt = (productName: string) => {
    navigator.clipboard.writeText(generateProductPrompt(productName));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const selectImportFiles = (files: FileList | null) => {
    const selected = Array.from(files || []);
    const totalSize = selected.reduce((sum, file) => sum + file.size, 0);
    if (selected.length < 1 || selected.length > 10) {
      showToast('Wybierz od 1 do 10 plików TXT/MD', 'error');
      return;
    }
    if (selected.some(file => !/\.(txt|md)$/i.test(file.name) || file.size > 2 * 1024 * 1024)) {
      showToast('Każdy plik musi mieć rozszerzenie TXT/MD i maksymalnie 2 MiB', 'error');
      return;
    }
    if (totalSize > 10 * 1024 * 1024) {
      showToast('Łączny rozmiar plików nie może przekroczyć 10 MiB', 'error');
      return;
    }
    setImportFiles(selected);
    setKnowledgeImport(null);
    setSelectedImportProposalIds(new Set());
  };

  const startKnowledgeImport = async () => {
    if (!selectedStoreId || importFiles.length === 0) return;
    try {
      setImportLoading(true);
      const response = await knowledgeBaseApi.createImport(selectedStoreId, importFiles);
      setKnowledgeImport(response.data.data as KnowledgeImportRecord);
      showToast('Analiza plików została uruchomiona');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Nie udało się uruchomić importu', 'error');
    } finally {
      setImportLoading(false);
    }
  };

  const toggleImportProposal = (proposalId: string) => {
    setSelectedImportProposalIds(current => {
      const next = new Set(current);
      if (next.has(proposalId)) next.delete(proposalId); else next.add(proposalId);
      return next;
    });
  };

  const applyImport = async () => {
    if (!knowledgeImport || selectedImportProposalIds.size === 0) return;
    try {
      setImportLoading(true);
      const proposals = knowledgeImport.proposals
        .filter(proposal => selectedImportProposalIds.has(proposal.id))
        .map(proposal => ({
          id: proposal.id,
          value: parseProposalValue(proposal.value, importEdits[proposal.id] ?? formatProposalValue(proposal.value)),
        }));
      await knowledgeBaseApi.applyImport(knowledgeImport.id, proposals);
      setKnowledgeImport(current => current ? { ...current, status: 'APPLIED', progress: 100 } : current);
      setImportFiles([]);
      await loadData();
      showToast('Zatwierdzone wartości zapisano w Bazie wiedzy');
    } catch (error: any) {
      showToast(error?.response?.data?.error || error?.message || 'Nie udało się zatwierdzić importu', 'error');
    } finally {
      setImportLoading(false);
    }
  };

  const loadLabProducts = async () => {
    if (loadingLabProducts) return;
    setLoadingLabProducts(true);
    try {
      const res = await knowledgeBaseApi.getLabProducts();
      setLabProducts(res.data.data || []);
    } catch {
      showToast('Błąd ładowania produktów Lab', 'error');
    } finally {
      setLoadingLabProducts(false);
    }
  };

  const loadSalesProducts = async (storeId: string) => {
    if (!storeId) return;
    setLoadingSalesProducts(true);
    try {
      const allProducts: Product[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        const res = await warehouseApi.getProducts({
          page,
          limit: 500,
          isActive: 'true',
          storeId,
          sortBy: 'name',
          sortOrder: 'asc',
        });
        allProducts.push(...(res.data.products || []));
        totalPages = res.data.pagination?.totalPages || 1;
        page += 1;
      } while (page <= totalPages);

      const products = allProducts
        .filter(product => Boolean(product.woocommerceProductId))
        .map(product => ({
          id: product.id,
          name: product.name,
          sku: product.sku,
          storeId,
          woocommerceProductId: product.woocommerceProductId,
        }));
      setSalesProducts(current => [
        ...current.filter(product => product.storeId !== storeId),
        ...products,
      ]);
    } catch {
      showToast('Błąd ładowania produktów z logistyki', 'error');
    } finally {
      setLoadingSalesProducts(false);
    }
  };

  const loadProductFaqBundles = async (storeId: string) => {
    if (!storeId) return;
    setLoadingProductFaqBundles(true);
    try {
      const res = await knowledgeBaseApi.getProductFaqBundles(storeId);
      setProductFaqBundles((res.data.data || []) as ProductFaqBundleOption[]);
    } catch {
      setProductFaqBundles([]);
      showToast('Błąd ładowania bundli z Kreatora', 'error');
    } finally {
      setLoadingProductFaqBundles(false);
    }
  };

  const openNewFaq = (storeId: string, mode: NewFaqMode = 'manual') => {
    setSelectedStoreId(storeId);
    setNewFaqMode(mode);
    setNewFaqData({ ...DEFAULT_NEW_FAQ_DATA, storeId });
    setImportFiles([]);
    setKnowledgeImport(null);
    setSelectedImportProposalIds(new Set());
    setImportEdits({});
    setShowNewFaq(true);
    if (mode !== 'import') {
      void loadSalesProducts(storeId);
      void loadProductFaqBundles(storeId);
    }
    if (mode === 'lab') void loadLabProducts();
  };

  const selectNewFaqMode = (mode: NewFaqMode) => {
    setNewFaqMode(mode);
    if (mode === 'lab') void loadLabProducts();
    if (mode !== 'import' && selectedStoreId) {
      void loadSalesProducts(selectedStoreId);
      void loadProductFaqBundles(selectedStoreId);
    }
  };

  const generateFromLab = async (labKnowledgeBaseId: string) => {
    const warehouseProductId = (newFaqData.warehouseProductId as string) || '';
    if (!warehouseProductId) {
      showToast('Wybierz produkt z logistyki, którego dotyczy FAQ', 'error');
      return;
    }
    try {
      setGeneratingFromLab(labKnowledgeBaseId);
      const res = await knowledgeBaseApi.generateFromLab(labKnowledgeBaseId, selectedStoreId || undefined, warehouseProductId);
      showToast(`FAQ wygenerowane (${res.data.tokensUsed} tokenów)`);
      setShowNewFaq(false);
      loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Błąd generowania FAQ z Laboratorium';
      showToast(msg, 'error');
    } finally {
      setGeneratingFromLab(null);
    }
  };

  // ============================================
  // Derived data
  // ============================================

  const policyItems = items.filter(i => i.sectionType && i.sectionType !== 'product_faq' && !i.storeId);
  const labProductsWithKnowledge = labProducts.filter(product => product.knowledgeBases.length > 0);

  const getEffectiveItem = (sectionType: string): { item: KnowledgeBaseItem } | null => {
    const item = policyItems.find(i => i.sectionType === sectionType);
    return item ? { item } : null;
  };

  // ============================================
  // Render
  // ============================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-success-bg to-success-bg border border-success-border flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-success" />
          </div>
          Baza wiedzy AI
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Uzupełnij sekcje, aby AI lepiej odpowiadał na wiadomości klientów. Każde pole można ustawić globalnie lub osobno per sklep.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
        </div>
      ) : !definitions ? (
        <EmptyState
          icon={BookOpen}
          title="Błąd ładowania definicji"
          description="Nie udało się załadować definicji sekcji bazy wiedzy."
          iconColorClass="from-danger-bg to-danger-bg border-danger-border text-danger"
        />
      ) : (
        <>
          {/* POLICY TILES */}
          <div className="animate-slide-up">
            <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
              Polityki sklepu
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {definitions.policies.map(def => {
                const effective = getEffectiveItem(def.sectionType);
                const item = effective?.item;
                const configData = item?.configData as Record<string, unknown> | null;
                const perStore = (configData?._perStore || {}) as Record<string, unknown>;
                const overriddenStoreCount = new Set(
                  Object.values(perStore).flatMap(value => (
                    value && typeof value === 'object' ? Object.keys(value as Record<string, unknown>) : []
                  )),
                ).size;
                const filled = countFilledFields(configData);
                const total = def.fields.length;
                const colorClass = COLOR_MAP[def.color] || COLOR_MAP.cyan;
                const IconComp = ICON_MAP[def.icon] || Info;
                const isEmpty = filled === 0;

                return (
                  <button
                    key={def.sectionType}
                    onClick={() => item && openPanel(item)}
                    className={`group relative p-4 rounded-xl border transition-all text-left ${
                      isEmpty
                        ? 'bg-surface-raised/30 border-border-subtle hover:border-border hover:bg-surface-raised/50'
                        : 'bg-surface-raised/50 border-border hover:border-border-strong hover:bg-surface-raised/70'
                    }`}
                  >
                    <div className="absolute top-2 right-2">
                      <span className="text-xxs px-1.5 py-0.5 rounded-full bg-accent-primary/10 text-accent-primary border border-accent-primary/20">
                        {overriddenStoreCount > 0 ? `Globalna + ${overriddenStoreCount} ${overriddenStoreCount === 1 ? 'sklep' : 'sklepy'}` : 'Globalna'}
                      </span>
                    </div>
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br border flex items-center justify-center mb-3 ${colorClass}`}>
                      <IconComp className="w-4 h-4" />
                    </div>
                    <h3 className="text-sm font-medium text-text-primary mb-1">{def.title}</h3>
                    <p className={`text-xs ${isEmpty ? 'text-text-disabled' : 'text-text-secondary'}`}>
                      {isEmpty ? 'Puste' : `${filled}/${total} pól`}
                    </p>
                  </button>
                );
              })}
            </div>

            {stores.length > 1 && (
              <p className="text-xs text-text-disabled mt-2">
                Kliknij kafelek, aby edytować. Każde pole można ustawić globalnie lub osobno per sklep.
              </p>
            )}
          </div>

          {/* FAQ produktów: każda marka ma widoczną własną sekcję bez przełączania kontekstu. */}
          <div className="grid grid-cols-1 items-start gap-5 animate-slide-up lg:grid-cols-2" style={{ animationDelay: '0.1s' }}>
            {stores.filter(store => store.isActive).map(store => {
              const storeFaqItems = items.filter(item => item.sectionType === 'product_faq' && item.storeId === store.id);
              return (
                <section key={store.id} className="rounded-xl border border-border-subtle bg-surface-raised/20 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <Store className="h-4 w-4 shrink-0 text-info" />
                      <h2 className="truncate text-sm font-semibold uppercase tracking-wider text-text-secondary">
                        FAQ Produktów - {store.name}
                      </h2>
                      <span className="rounded-md bg-surface-hover px-1.5 py-0.5 text-[10px] tabular-nums text-text-muted">
                        {storeFaqItems.length}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => openNewFaq(store.id, 'manual')}
                      className="btn-secondary flex shrink-0 items-center gap-1.5 text-xs"
                    >
                      <Plus className="h-3.5 w-3.5" /> Dodaj FAQ
                    </button>
                  </div>

                  {storeFaqItems.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border-subtle bg-surface-base/20">
                      <EmptyState
                        icon={Package}
                        title={`Brak FAQ produktów dla ${store.name}`}
                        description="Dodaj FAQ ręcznie, z Laboratorium albo z plików TXT/MD."
                        size="sm"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
                      {storeFaqItems.map(item => {
                        const config = (item.configData || {}) as Record<string, unknown>;
                        const productName = (config.productName as string) || 'Bez nazwy';
                        const filled = countFilledProductFaqSections(config);
                        const total = 6;
                        return (
                          <div
                            key={item.id}
                            className="group relative rounded-xl border border-border-subtle bg-surface-raised/40 p-4 transition-all hover:border-info-border hover:bg-surface-raised/60"
                          >
                            <button onClick={() => openPanel(item)} className="w-full text-left">
                              <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg border bg-gradient-to-br ${COLOR_MAP.purple}`}>
                                <Package className="h-4 w-4" />
                              </div>
                              <h3 className="mb-1 truncate text-sm font-medium text-text-primary">{productName}</h3>
                              <p className="text-xs text-text-secondary">{filled}/{total} pól</p>
                            </button>
                            <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                              <button
                                onClick={(event) => { event.stopPropagation(); copyPrompt(productName); }}
                                className="rounded-lg p-1.5 text-text-muted transition-all hover:bg-accent-primary/10 hover:text-accent-primary"
                                title="Kopiuj prompt do zewnętrznego AI"
                              >
                                {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                              </button>
                              <button
                                onClick={(event) => { event.stopPropagation(); void deleteProductFaq(item); }}
                                className="rounded-lg p-1.5 text-text-muted transition-all hover:bg-danger/10 hover:text-danger"
                                title="Usuń FAQ"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>

        </>
      )}

      {/* ============================================ */}
      {/* EDIT SECTION MODAL */}
      {/* ============================================ */}
      {activePanel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
          {...activePanelBackdropProps}
        >
          <div
            className="relative w-full max-w-3xl bg-surface-raised rounded-xl border border-border shadow-2xl mx-4 max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="p-5 pb-0">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {(() => {
                    const IconComp = ICON_MAP[activePanel.definition.icon] || Info;
                    const colorClass = COLOR_MAP[activePanel.definition.color] || COLOR_MAP.cyan;
                    return (
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br border flex items-center justify-center ${colorClass}`}>
                        <IconComp className="w-4 h-4" />
                      </div>
                    );
                  })()}
                  <div>
                    <h3 className="text-base font-semibold text-text-primary">{activePanel.definition.title}</h3>
                    {activePanel.item.sectionType === 'product_faq' && (
                      <p className="text-xs text-text-secondary">{(formData.productName as string) || 'FAQ produktu'}</p>
                    )}
                  </div>
                </div>
                <button onClick={() => setActivePanel(null)} className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-all">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto max-h-[calc(85vh-200px)] px-5 pb-2">
              <div className="space-y-4">
                {activePanel.item.sectionType === 'product_faq' && (
                  <>
                  <ProductFaqMatchingEditor
                    data={formData}
                    products={salesProducts.filter(product => product.storeId === selectedStoreId)}
                    loading={loadingSalesProducts}
                    onChange={setFormData}
                  />
                  <ProductFaqPricingEditor
                    data={formData}
                    bundles={productFaqBundles}
                    loadingBundles={loadingProductFaqBundles}
                    onChange={setFormData}
                  />
                  </>
                )}

                {activePanel.item.sectionType !== 'product_faq' && (
                  <div className="flex items-start gap-2.5 rounded-lg border border-accent-primary/20 bg-accent-primary/5 p-3 text-xs text-text-secondary">
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent-primary" />
                    <p>
                      Powiązany szablon ma pierwszeństwo, gdy wiadomość klienta pasuje do opisanej sytuacji. Jeśli nie pasuje, AI pomija szablon i tworzy odpowiedź samodzielnie na podstawie instrukcji poniżej.
                    </p>
                  </div>
                )}

                {activePanel.definition.fields
                  .filter(field => activePanel.item.sectionType !== 'product_faq' || !PRODUCT_FAQ_CUSTOM_FIELD_KEYS.has(field.key))
                  .map(field => {
                  const perStore = (formData._perStore as Record<string, Record<string, unknown>> | undefined);
                  const isPerStore = !!perStore?.[field.key];
                  const supportsPerStore = ['text', 'number', 'select', 'textarea'].includes(field.type) && stores.length > 1;
                  const supportsTemplateBinding = TEMPLATE_BINDING_FIELD_KEYS.has(field.key)
                    && activePanel.item.sectionType !== 'product_faq';

                  const toggleScope = () => {
                    if (isPerStore) {
                      // Switch to global — remove per-store overrides
                      setFormData(prev => {
                        const ps = { ...(prev._perStore as Record<string, Record<string, unknown>> || {}) };
                        delete ps[field.key];
                        let next: Record<string, unknown> = { ...prev, _perStore: ps };
                        if (Object.keys(ps).length === 0) delete next._perStore;
                        next = clearPerStoreTemplateBindings(next, field.key);
                        return next;
                      });
                    } else {
                      // Switch to per-store — initialize each store with the global value
                      const globalVal = formData[field.key];
                      const storeValues: Record<string, unknown> = {};
                      stores.forEach(s => { storeValues[s.id] = globalVal ?? ''; });
                      setFormData(prev => ({
                        ...prev,
                        _perStore: {
                          ...(prev._perStore as Record<string, Record<string, unknown>> || {}),
                          [field.key]: storeValues,
                        },
                      }));
                    }
                  };

                  const updatePerStoreValue = (storeId: string, val: unknown) => {
                    setFormData(prev => ({
                      ...prev,
                      _perStore: {
                        ...(prev._perStore as Record<string, Record<string, unknown>> || {}),
                        [field.key]: {
                          ...((prev._perStore as Record<string, Record<string, unknown>> || {})[field.key] || {}),
                          [storeId]: val,
                        },
                      },
                    }));
                  };

                  if (supportsPerStore && isPerStore) {
                    const storeValues = perStore?.[field.key] || {};
                    return (
                      <div key={field.key}>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs text-text-secondary">{field.label}</label>
                          <button
                            onClick={toggleScope}
                            className="flex items-center gap-1 text-xxs px-2 py-0.5 rounded-full bg-accent-primary/10 text-accent-primary border border-accent-primary/20 hover:bg-accent-primary/20 transition-all"
                          >
                            <Store className="w-3 h-3" />
                            Per sklep
                          </button>
                        </div>
                        <div className="space-y-2 pl-0.5">
                          {stores.map(store => (
                            <div key={store.id} className="flex items-start gap-2">
                              <span className="text-xs text-text-muted w-28 flex-shrink-0 pt-2 truncate" title={store.name}>
                                {store.name}:
                              </span>
                              <div className="flex-1">
                                {supportsTemplateBinding && (
                                  <TemplateBindingSelect
                                    templates={messageTemplates}
                                    storeId={store.id}
                                    value={getTemplateBindingValue(formData, field.key, store.id)}
                                    onChange={(templateId) => setFormData(prev => (
                                      setTemplateBindingValue(prev, field.key, templateId, store.id)
                                    ))}
                                  />
                                )}
                                {field.type === 'textarea' ? (
                                  <textarea
                                    value={(storeValues[store.id] as string) || ''}
                                    onChange={(e) => updatePerStoreValue(store.id, e.target.value)}
                                    className="input w-full min-h-[60px] resize-y text-xs"
                                    placeholder={field.placeholder}
                                    rows={2}
                                  />
                                ) : field.type === 'number' ? (
                                  <input
                                    type="number"
                                    value={storeValues[store.id] !== undefined && storeValues[store.id] !== null ? String(storeValues[store.id]) : ''}
                                    onChange={(e) => updatePerStoreValue(store.id, e.target.value ? Number(e.target.value) : undefined)}
                                    className="input w-full text-xs"
                                    placeholder={field.placeholder}
                                  />
                                ) : field.type === 'select' ? (
                                  <Select
                                    value={storeValues[store.id] !== undefined && storeValues[store.id] !== null ? String(storeValues[store.id]) : ''}
                                    onChange={(e) => {
                                      const allNumeric = (field.options || []).every(o => !isNaN(Number(o.value)));
                                      updatePerStoreValue(store.id, allNumeric && e.target.value ? Number(e.target.value) : e.target.value);
                                    }}
                                    className="input w-full text-xs"
                                  >
                                    <option value="">Wybierz...</option>
                                    {(field.options || []).map(o => (
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                  </Select>
                                ) : (
                                  <input
                                    type="text"
                                    value={(storeValues[store.id] as string) || ''}
                                    onChange={(e) => updatePerStoreValue(store.id, e.target.value)}
                                    className="input w-full text-xs"
                                    placeholder={field.placeholder}
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Global fallback hint */}
                        <p className="text-xxs text-text-disabled mt-1 pl-0.5">
                          Wartość globalna (fallback): {formData[field.key] ? String(formData[field.key]) : '(puste)'}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div key={field.key}>
                      {supportsPerStore ? (
                        <>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs text-text-secondary">{field.label}</label>
                            <button
                              onClick={toggleScope}
                              className="flex items-center gap-1 text-xxs px-2 py-0.5 rounded-full bg-surface-hover/50 text-text-muted border border-border-subtle hover:text-accent-primary hover:border-accent-primary/20 transition-all"
                            >
                              <Globe className="w-3 h-3" />
                              Globalnie
                            </button>
                          </div>
                          {supportsTemplateBinding && (
                            <TemplateBindingSelect
                              templates={messageTemplates}
                              value={getTemplateBindingValue(formData, field.key)}
                              onChange={(templateId) => setFormData(prev => (
                                setTemplateBindingValue(prev, field.key, templateId)
                              ))}
                            />
                          )}
                          <SectionField
                            field={field}
                            value={formData[field.key]}
                            onChange={(val) => setFormData(prev => ({ ...prev, [field.key]: val }))}
                            hideLabel
                          />
                        </>
                      ) : supportsTemplateBinding ? (
                        <>
                          <label className="mb-1.5 block text-xs text-text-secondary">{field.label}</label>
                          <TemplateBindingSelect
                            templates={messageTemplates}
                            value={getTemplateBindingValue(formData, field.key)}
                            onChange={(templateId) => setFormData(prev => (
                              setTemplateBindingValue(prev, field.key, templateId)
                            ))}
                          />
                          <SectionField
                            field={field}
                            value={formData[field.key]}
                            onChange={(val) => setFormData(prev => ({ ...prev, [field.key]: val }))}
                            hideLabel
                          />
                        </>
                      ) : (
                        <SectionField
                          field={field}
                          value={formData[field.key]}
                          onChange={(val) => setFormData(prev => ({ ...prev, [field.key]: val }))}
                        />
                      )}
                    </div>
                  );
                })}

                {/* Product FAQ: copy prompt button */}
                {activePanel.item.sectionType === 'product_faq' && (formData.productName as string) && (
                  <button
                    onClick={() => copyPrompt(formData.productName as string)}
                    className="w-full flex items-center justify-center gap-2 p-2.5 rounded-lg border border-dashed border-border text-xs text-text-secondary hover:text-accent-primary hover:border-accent-primary/30 transition-all"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Skopiowano!' : 'Kopiuj prompt do zewnętrznego AI'}
                  </button>
                )}
              </div>
            </div>

            {/* Modal footer */}
            <div className="p-5 pt-4 border-t border-border-subtle flex gap-3">
              <button
                onClick={savePanel}
                disabled={saving}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Zapisz
              </button>
              <button onClick={() => setActivePanel(null)} className="btn-secondary flex-1">
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* NEW PRODUCT FAQ MODAL */}
      {/* ============================================ */}
      {showNewFaq && definitions && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
          {...newFaqBackdropProps}
        >
          <div
            className="relative w-full max-w-3xl bg-surface-raised rounded-xl border border-border shadow-2xl mx-4 max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="p-5 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br border flex items-center justify-center ${COLOR_MAP.purple}`}>
                    <Package className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-text-primary">Dodaj wiedzę produktu</h3>
                    <p className="text-xs text-text-muted">
                      {stores.find(store => store.id === selectedStoreId)?.name || 'Wybrany sklep'} - wybierz źródło danych
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => !generatingFromLab && setShowNewFaq(false)}
                  disabled={!!generatingFromLab}
                  className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 disabled:opacity-40 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto max-h-[calc(85vh-160px)] px-5 pb-2">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => selectNewFaqMode('manual')}
                    className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                      newFaqMode === 'manual'
                        ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-primary'
                        : 'border-border-subtle bg-surface-hover/20 text-text-secondary hover:text-text-primary hover:border-accent-primary/30'
                    }`}
                  >
                    <Plus className="w-4 h-4 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Uzupełnij ręcznie</p>
                      <p className="text-[10px] text-text-muted">Wpisz pytania i odpowiedzi samodzielnie</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => selectNewFaqMode('lab')}
                    className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                      newFaqMode === 'lab'
                        ? 'border-info-border bg-info-bg text-info'
                        : 'border-border-subtle bg-surface-hover/20 text-text-secondary hover:text-text-primary hover:border-info-border'
                    }`}
                  >
                    <FlaskConical className="w-4 h-4 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Generuj z Laboratorium</p>
                      <p className="text-[10px] text-text-muted">Na podstawie gotowej bazy wiedzy</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => selectNewFaqMode('import')}
                    className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                      newFaqMode === 'import'
                        ? 'border-info-border bg-info-bg text-info'
                        : 'border-border-subtle bg-surface-hover/20 text-text-secondary hover:text-text-primary hover:border-info-border'
                    }`}
                  >
                    <Upload className="w-4 h-4 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Zewnętrzna Baza wiedzy</p>
                      <p className="text-[10px] text-text-muted">Import plików TXT i MD z podglądem</p>
                    </div>
                  </button>
                </div>

                {newFaqMode === 'manual' ? (
                  <>
                    <ProductFaqMatchingEditor
                      data={newFaqData}
                      products={salesProducts.filter(product => product.storeId === selectedStoreId)}
                      loading={loadingSalesProducts}
                      onChange={setNewFaqData}
                    />

                    <ProductFaqPricingEditor
                      data={newFaqData}
                      bundles={productFaqBundles}
                      loadingBundles={loadingProductFaqBundles}
                      onChange={setNewFaqData}
                    />

                    {definitions.productFaq.fields.filter(field => !PRODUCT_FAQ_CUSTOM_FIELD_KEYS.has(field.key)).map(field => (
                      <SectionField
                        key={field.key}
                        field={field}
                        value={newFaqData[field.key]}
                        onChange={(val) => setNewFaqData(prev => ({ ...prev, [field.key]: val }))}
                      />
                    ))}

                    {(newFaqData.productName as string) && (
                      <button
                        onClick={() => copyPrompt(newFaqData.productName as string)}
                        className="w-full flex items-center justify-center gap-2 p-2.5 rounded-lg border border-dashed border-border text-xs text-text-secondary hover:text-accent-primary hover:border-accent-primary/30 transition-all"
                      >
                        {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                        {copied ? 'Skopiowano!' : 'Kopiuj prompt do zewnętrznego AI'}
                      </button>
                    )}
                  </>
                ) : newFaqMode === 'lab' ? (
                  <>
                    <ProductFaqProductSelect
                      value={(newFaqData.warehouseProductId as string) || ''}
                      products={salesProducts.filter(product => product.storeId === selectedStoreId)}
                      loading={loadingSalesProducts}
                      onChange={(productId) => setNewFaqData(current => (
                        withSelectedBaseProduct(current, productId, salesProducts)
                      ))}
                    />

                    <div className="rounded-xl border border-border-subtle bg-surface-raised/30 overflow-hidden">
                    {loadingLabProducts ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-info" />
                      </div>
                    ) : labProductsWithKnowledge.length === 0 ? (
                      <div className="text-center py-12 px-4">
                        <FlaskConical className="w-10 h-10 text-text-disabled mx-auto mb-3" />
                        <p className="text-sm text-text-secondary">Brak produktów z gotową bazą wiedzy</p>
                        <p className="text-xs text-text-disabled mt-1">
                          Dodaj produkt do Laboratorium i wygeneruj bazę wiedzy, aby użyć tej funkcji.
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-[46vh] overflow-y-auto">
                        {labProductsWithKnowledge.map(product => (
                          <div key={product.id} className="border-b border-border-subtle last:border-b-0">
                            <div className="flex items-center gap-3 p-3">
                              {product.imageUrl ? (
                                <img src={product.imageUrl} alt={product.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-surface-hover/50 flex items-center justify-center flex-shrink-0">
                                  <Package className="w-5 h-5 text-text-disabled" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-text-primary truncate">{product.name}</h4>
                                <p className="text-xs text-text-muted">
                                  {product.knowledgeBases.length} {product.knowledgeBases.length === 1 ? 'baza wiedzy' : 'bazy wiedzy'}
                                </p>
                              </div>
                            </div>

                            <div className="border-t border-border-subtle">
                              {product.knowledgeBases.map(kb => {
                                const isGenerating = generatingFromLab === kb.id;
                                const hasSalesProduct = Boolean(newFaqData.warehouseProductId as string);
                                return (
                                  <button
                                    key={kb.id}
                                    onClick={() => !generatingFromLab && hasSalesProduct && generateFromLab(kb.id)}
                                    disabled={!!generatingFromLab || !hasSalesProduct}
                                    className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-all ${
                                      isGenerating
                                        ? 'bg-info-bg'
                                        : generatingFromLab || !hasSalesProduct
                                          ? 'opacity-40 cursor-not-allowed'
                                          : 'hover:bg-surface-hover/30'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Sparkles className={`w-3.5 h-3.5 flex-shrink-0 ${isGenerating ? 'text-info animate-pulse' : 'text-text-muted'}`} />
                                      <span className="text-xs text-text-secondary truncate">{kb.name}</span>
                                      <span className="text-xxs text-text-disabled flex-shrink-0">
                                        {formatKnowledgeBaseDateTime(kb.updatedAt)}
                                      </span>
                                    </div>
                                    <div className="flex-shrink-0 ml-2">
                                      {isGenerating ? (
                                        <Loader2 className="w-4 h-4 animate-spin text-info" />
                                      ) : (
                                        <span className="text-xs text-info">
                                          {hasSalesProduct ? 'Generuj' : 'Wybierz produkt'}
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    </div>
                  </>
                ) : (
                  <div className="space-y-3 rounded-xl border border-border-subtle bg-surface-raised/30 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h4 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                          <FileText className="h-4 w-4 text-info" />
                          Zewnętrzna baza wiedzy
                        </h4>
                        <p className="mt-1 text-xs text-text-muted">
                          Do 10 plików TXT/MD, maksymalnie 2 MiB na plik i 10 MiB łącznie. Zapis nastąpi dopiero po zatwierdzeniu propozycji.
                        </p>
                      </div>
                      <label className="btn-secondary flex shrink-0 cursor-pointer items-center gap-1.5 text-xs">
                        <Upload className="h-3.5 w-3.5" /> Wybierz pliki
                        <input
                          type="file"
                          accept=".txt,.md,text/plain,text/markdown"
                          multiple
                          className="sr-only"
                          onChange={(event) => selectImportFiles(event.target.files)}
                        />
                      </label>
                    </div>

                    {importFiles.length > 0 && !knowledgeImport && (
                      <div className="rounded-lg border border-border-subtle bg-surface-base/30 p-3">
                        <div className="flex flex-wrap gap-1.5">
                          {importFiles.map(file => (
                            <span key={`${file.name}-${file.size}`} className="rounded-md bg-surface-hover px-2 py-1 text-xxs text-text-secondary">
                              {file.name} ({Math.ceil(file.size / 1024)} KiB)
                            </span>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={startKnowledgeImport}
                          disabled={importLoading}
                          className="btn-primary mt-3 flex items-center gap-1.5 text-xs"
                        >
                          {importLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5" />}
                          Analizuj dla {stores.find(store => store.id === selectedStoreId)?.name || 'wybranego sklepu'}
                        </button>
                      </div>
                    )}

                    {knowledgeImport && ['PENDING', 'PROCESSING'].includes(knowledgeImport.status) && (
                      <div className="rounded-lg border border-accent-primary/20 bg-accent-primary/5 p-3">
                        <div className="flex items-center justify-between text-xs text-text-secondary">
                          <span className="flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-accent-primary" /> Analiza w toku</span>
                          <span>{knowledgeImport.progress}%</span>
                        </div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-hover">
                          <div className="h-full bg-accent-primary transition-all" style={{ width: `${knowledgeImport.progress}%` }} />
                        </div>
                      </div>
                    )}

                    {knowledgeImport?.status === 'FAILED' && (
                      <div className="rounded-lg border border-danger-border bg-danger-bg p-3 text-xs text-danger">
                        Analiza nie powiodła się: {knowledgeImport.error || 'nieznany błąd'}
                      </div>
                    )}

                    {knowledgeImport?.status === 'READY' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs text-text-secondary">
                            Znaleziono {knowledgeImport.proposals.length} propozycji. Konflikty są domyślnie odznaczone.
                          </p>
                          <span className="shrink-0 text-xxs text-text-disabled">Wybrano {selectedImportProposalIds.size}</span>
                        </div>
                        <div className="max-h-[34rem] space-y-2 overflow-y-auto pr-1">
                          {knowledgeImport.proposals.map(proposal => (
                            <div key={proposal.id} className={`rounded-lg border p-3 ${proposal.conflict ? 'border-warning/30 bg-warning/5' : 'border-border-subtle bg-surface-base/20'}`}>
                              <div className="flex items-start gap-2">
                                <input
                                  type="checkbox"
                                  checked={selectedImportProposalIds.has(proposal.id)}
                                  onChange={() => toggleImportProposal(proposal.id)}
                                  className="mt-1 rounded border-border"
                                  aria-label={`Uwzględnij propozycję ${proposal.fieldKey}`}
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-medium text-text-primary">{proposal.productName || proposal.productSku || proposal.sectionType} · {proposal.fieldKey}</span>
                                    <span className="text-xxs text-text-muted">Pewność {Math.round(proposal.confidence * 100)}%</span>
                                    {proposal.conflict && <span className="text-xxs text-warning">Konflikt z obecną wartością</span>}
                                  </div>
                                  <label className="mt-2 block text-xxs text-text-muted">Proponowana wartość</label>
                                  {typeof proposal.value === 'string' && proposal.value.length < 140 ? (
                                    <input
                                      className="input mt-1 w-full text-xs"
                                      value={importEdits[proposal.id] ?? formatProposalValue(proposal.value)}
                                      onChange={event => setImportEdits(current => ({ ...current, [proposal.id]: event.target.value }))}
                                    />
                                  ) : (
                                    <textarea
                                      className="input mt-1 min-h-[72px] w-full resize-y font-mono text-xs"
                                      value={importEdits[proposal.id] ?? formatProposalValue(proposal.value)}
                                      onChange={event => setImportEdits(current => ({ ...current, [proposal.id]: event.target.value }))}
                                    />
                                  )}
                                  <p className="mt-2 text-xxs text-text-muted">
                                    Obecna wartość: <span className="text-text-secondary">{proposal.currentValue == null || proposal.currentValue === '' ? 'brak' : formatProposalValue(proposal.currentValue)}</span>
                                  </p>
                                  <p className="mt-1 text-xxs italic text-text-disabled">Źródło: „{proposal.sourceExcerpt}”</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {knowledgeImport?.status === 'APPLIED' && (
                      <p className="text-xs text-success">Import zatwierdzony. Wartości są dostępne dla tego sklepu.</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Modal footer */}
            <div className="p-5 pt-4 border-t border-border-subtle flex gap-3">
              {newFaqMode === 'manual' && (
                <button
                  onClick={createProductFaq}
                  disabled={savingNewFaq || !(newFaqData.productName as string)}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {savingNewFaq ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Dodaj FAQ
                </button>
              )}
              {newFaqMode === 'import' && knowledgeImport?.status === 'READY' && (
                <button
                  type="button"
                  onClick={applyImport}
                  disabled={importLoading || selectedImportProposalIds.size === 0}
                  className="btn-primary flex flex-1 items-center justify-center gap-2"
                >
                  {importLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Zatwierdź i zapisz wybrane
                </button>
              )}
              <button
                onClick={() => !generatingFromLab && setShowNewFaq(false)}
                disabled={!!generatingFromLab}
                className="btn-secondary flex-1"
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl border shadow-lg ${
          toast.type === 'success'
            ? 'bg-success-bg border-success-border text-success'
            : 'bg-danger-bg border-danger-border text-danger'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

// ============================================
// Message template bindings
// ============================================

function TemplateBindingSelect({
  templates,
  storeId,
  value,
  onChange,
}: {
  templates: MessageTemplateOption[];
  storeId?: string;
  value: string;
  onChange: (templateId: string) => void;
}) {
  const availableTemplates = templates.filter(template => (
    template.isActive && (storeId ? !template.storeId || template.storeId === storeId : !template.storeId)
  ));

  return (
    <div className="mb-2">
      <p className="mb-1 flex items-center gap-1.5 text-xxs text-text-muted">
        <FileText className="h-3.5 w-3.5 shrink-0 text-text-disabled" />
        Preferowany szablon odpowiedzi
      </p>
      <div className="min-w-0">
        <Select
          value={value}
          disabled={availableTemplates.length === 0}
          onChange={(event) => onChange(event.target.value)}
          className="!py-1.5 !text-xs"
          placeholder={availableTemplates.length > 0 ? 'Bez powiązanego szablonu' : 'Brak aktywnych szablonów odpowiedzi'}
        >
          <option value="">
            {availableTemplates.length > 0 ? 'Bez powiązanego szablonu' : 'Brak aktywnych szablonów odpowiedzi'}
          </option>
          {availableTemplates.map(template => (
            <option key={template.id} value={template.id}>
              {template.name}{template.storeId ? ` (${template.store?.name || 'sklep'})` : ' (globalny)'}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

// ============================================
// Product FAQ matching and pricing
// ============================================

function ProductFaqMatchingEditor({
  data,
  products,
  loading,
  onChange,
}: {
  data: Record<string, unknown>;
  products: SalesProductOption[];
  loading: boolean;
  onChange: Dispatch<SetStateAction<Record<string, unknown>>>;
}) {
  const baseProductId = typeof data.warehouseProductId === 'string' ? data.warehouseProductId : '';
  const skuPrefix = typeof data.skuPrefix === 'string' ? data.skuPrefix : '';
  const includedIds = Array.isArray(data.includedWooCommerceProductIds)
    ? data.includedWooCommerceProductIds as string[]
    : [];
  const excludedIds = Array.isArray(data.excludedWooCommerceProductIds)
    ? data.excludedWooCommerceProductIds as string[]
    : [];
  const excludedSet = new Set(excludedIds);
  const includedSet = new Set(includedIds);
  const baseProduct = products.find(product => product.id === baseProductId);
  const prefixMatches = skuPrefix
    ? products.filter(product => product.sku && skuMatchesFamilyPrefix(product.sku, skuPrefix))
    : [];
  const manuallyIncluded = products.filter(product => (
    product.woocommerceProductId && includedSet.has(product.woocommerceProductId)
  ));
  const excludedProducts = products.filter(product => (
    product.woocommerceProductId && excludedSet.has(product.woocommerceProductId)
  ));
  const coveredProductIds = new Set([
    ...(baseProduct ? [baseProduct.id] : []),
    ...prefixMatches
      .filter(product => !product.woocommerceProductId || !excludedSet.has(product.woocommerceProductId))
      .map(product => product.id),
    ...manuallyIncluded
      .filter(product => !product.woocommerceProductId || !excludedSet.has(product.woocommerceProductId))
      .map(product => product.id),
  ]);
  const availableManualProducts = products.filter(product => (
    product.id !== baseProductId
    && product.woocommerceProductId
    && !includedSet.has(product.woocommerceProductId)
  ));

  const updateList = (key: 'includedWooCommerceProductIds' | 'excludedWooCommerceProductIds', values: string[]) => {
    onChange(current => ({ ...current, [key]: [...new Set(values)] }));
  };

  const toggleExclusion = (woocommerceProductId: string) => {
    updateList(
      'excludedWooCommerceProductIds',
      excludedSet.has(woocommerceProductId)
        ? excludedIds.filter(id => id !== woocommerceProductId)
        : [...excludedIds, woocommerceProductId],
    );
  };

  return (
    <div className="space-y-3 rounded-xl border border-border-subtle bg-surface-base/20 p-4">
      <div>
        <h4 className="text-sm font-semibold text-text-primary">Produkty objęte FAQ</h4>
        <p className="mt-1 text-xs text-text-muted">
          Produkt bazowy nadaje nazwę FAQ. Reguła SKU automatycznie obejmie obecne i przyszłe warianty A/B.
        </p>
      </div>

      <ProductFaqProductSelect
        value={baseProductId}
        products={products}
        loading={loading}
        onChange={(productId) => onChange(current => {
          const next = withSelectedBaseProduct(current, productId, products);
          const nextProductId = next.woocommerceProductId as string | undefined;
          if (!nextProductId) return next;
          return {
            ...next,
            excludedWooCommerceProductIds: excludedIds.filter(id => id !== nextProductId),
          };
        })}
      />

      <div>
        <label htmlFor="product-faq-sku-prefix" className="mb-1 block text-xs text-text-secondary">
          SKU zaczyna się od
        </label>
        <input
          id="product-faq-sku-prefix"
          type="text"
          value={skuPrefix}
          onChange={(event) => onChange(current => ({ ...current, skuPrefix: event.target.value }))}
          className="input w-full"
          placeholder="np. lg_platki"
        />
        <p className="mt-1 text-xxs text-text-disabled">
          `lg_platki` obejmie `lg_platki`, `lg_platki_ab1` i `lg_platki-test`, ale nie `lg_platki2`.
        </p>
      </div>

      <div className="rounded-lg border border-info-border bg-info-bg/40 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-medium text-info">
            Reguła obejmuje obecnie {coveredProductIds.size} {coveredProductIds.size === 1 ? 'produkt' : 'produktów'}
          </p>
          <span className="text-xxs text-text-muted">Nowe pasujące SKU zostaną uwzględnione automatycznie</span>
        </div>

        {prefixMatches.length > 0 ? (
          <div className="mt-2 max-h-40 space-y-1 overflow-y-auto pr-1">
            {prefixMatches.map(product => {
              const externalId = product.woocommerceProductId || '';
              const isBase = product.id === baseProductId;
              const isExcluded = Boolean(externalId && excludedSet.has(externalId));
              return (
                <div key={product.id} className="flex items-center justify-between gap-2 rounded-md bg-surface-raised/60 px-2.5 py-2">
                  <div className="min-w-0">
                    <p className={`truncate text-xs ${isExcluded ? 'text-text-disabled line-through' : 'text-text-secondary'}`}>{product.name}</p>
                    <p className="truncate text-xxs text-text-muted">{product.sku || 'Brak SKU'}{isBase ? ' · produkt bazowy' : ''}</p>
                  </div>
                  {!isBase && externalId && (
                    <button
                      type="button"
                      onClick={() => toggleExclusion(externalId)}
                      className={`shrink-0 rounded-md px-2 py-1 text-xxs transition-colors ${
                        isExcluded
                          ? 'bg-accent-primary/10 text-accent-primary hover:bg-accent-primary/20'
                          : 'text-text-muted hover:bg-danger/10 hover:text-danger'
                      }`}
                    >
                      {isExcluded ? 'Przywróć' : 'Wyklucz'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-2 text-xs text-text-muted">
            {skuPrefix ? 'Obecnie żaden zsynchronizowany produkt nie pasuje do tej reguły.' : 'Wpisz prefiks SKU, aby zobaczyć automatyczne dopasowania.'}
          </p>
        )}
        {excludedProducts.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5 border-t border-info-border/40 pt-2">
            {excludedProducts.map(product => (
              <button
                key={product.id}
                type="button"
                onClick={() => toggleExclusion(product.woocommerceProductId || '')}
                className="rounded-md bg-surface-raised/60 px-2 py-1 text-xxs text-text-muted hover:text-accent-primary"
              >
                Przywróć: {product.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label htmlFor="product-faq-manual-product" className="mb-1 block text-xs text-text-secondary">
          Dodatkowo uwzględnij pojedyncze produkty
        </label>
        <Select
          id="product-faq-manual-product"
          value=""
          disabled={loading || availableManualProducts.length === 0}
          onChange={(event) => {
            const selectedId = event.target.value;
            if (!selectedId) return;
            onChange(current => ({
              ...current,
              includedWooCommerceProductIds: [...new Set([...includedIds, selectedId])],
              excludedWooCommerceProductIds: excludedIds.filter(id => id !== selectedId),
            }));
          }}
          placeholder="Dodaj produkt spoza reguły SKU"
        >
          <option value="">Dodaj produkt spoza reguły SKU</option>
          {availableManualProducts.map(product => (
            <option key={product.id} value={product.woocommerceProductId || ''}>
              {product.name}{product.sku ? ` (${product.sku})` : ''}
            </option>
          ))}
        </Select>
        {manuallyIncluded.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {manuallyIncluded.map(product => (
              <span key={product.id} className="flex items-center gap-1 rounded-lg border border-accent-primary/20 bg-accent-primary/10 px-2 py-1 text-xs text-accent-primary">
                {product.name}
                <button
                  type="button"
                  onClick={() => updateList(
                    'includedWooCommerceProductIds',
                    includedIds.filter(id => id !== product.woocommerceProductId),
                  )}
                  className="rounded p-0.5 hover:bg-accent-primary/20"
                  aria-label={`Usuń ${product.name} z dodatkowych produktów`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProductFaqPricingEditor({
  data,
  bundles,
  loadingBundles,
  onChange,
}: {
  data: Record<string, unknown>;
  bundles: ProductFaqBundleOption[];
  loadingBundles: boolean;
  onChange: Dispatch<SetStateAction<Record<string, unknown>>>;
}) {
  const pricingMode = data.pricingMode === 'bundle' ? 'bundle' : 'manual';
  const bundleId = typeof data.bundleId === 'string' ? data.bundleId : '';
  const bundleCurrency = typeof data.currency === 'string' && data.currency ? data.currency : 'PLN';
  const selectedBundle = bundles.find(bundle => bundle.id === bundleId);
  const priceVariants = Array.isArray(data.priceVariants)
    ? data.priceVariants as ProductFaqPriceVariant[]
    : [];

  const updateVariant = (id: string, values: Partial<ProductFaqPriceVariant>) => {
    onChange(current => ({
      ...current,
      priceVariants: priceVariants.map(variant => variant.id === id ? { ...variant, ...values } : variant),
    }));
  };

  const addPriceVariant = () => {
    onChange(current => ({
      ...current,
      priceVariants: [
        ...priceVariants,
        {
          id: createPriceVariantId(),
          name: `${priceVariants.length + 1} ${priceVariants.length === 0 ? 'sztuka' : 'szt.'}`,
          quantity: priceVariants.length + 1,
          currency: 'PLN',
        },
      ],
    }));
  };

  return (
    <div className="space-y-3 rounded-xl border border-border-subtle bg-surface-base/20 p-4">
      <div>
        <h4 className="text-sm font-semibold text-text-primary">Cennik produktu</h4>
        <p className="mt-1 text-xs text-text-muted">Wybierz aktualizowany automatycznie bundle albo utrzymuj warianty ręcznie.</p>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => onChange(current => ({ ...current, pricingMode: 'bundle' }))}
          className={`rounded-lg border p-3 text-left transition-colors ${
            pricingMode === 'bundle'
              ? 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary'
              : 'border-border-subtle bg-surface-raised/30 text-text-secondary hover:border-border'
          }`}
        >
          <p className="text-xs font-medium">Podłącz bundle z Kreatora</p>
          <p className="mt-1 text-xxs text-text-muted">AI pobiera aktualne ceny przy każdej odpowiedzi</p>
        </button>
        <button
          type="button"
          onClick={() => onChange(current => ({ ...current, pricingMode: 'manual' }))}
          className={`rounded-lg border p-3 text-left transition-colors ${
            pricingMode === 'manual'
              ? 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary'
              : 'border-border-subtle bg-surface-raised/30 text-text-secondary hover:border-border'
          }`}
        >
          <p className="text-xs font-medium">Warianty ręczne</p>
          <p className="mt-1 text-xxs text-text-muted">Dodaj dowolną liczbę wariantów cenowych</p>
        </button>
      </div>

      {pricingMode === 'bundle' ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_110px]">
            <div>
              <label htmlFor="product-faq-bundle" className="mb-1 block text-xs text-text-secondary">Bundle</label>
              <Select
                id="product-faq-bundle"
                value={bundleId}
                disabled={loadingBundles || bundles.length === 0}
                onChange={(event) => onChange(current => ({ ...current, bundleId: event.target.value }))}
                placeholder={loadingBundles ? 'Ładowanie bundli...' : 'Wybierz bundle'}
              >
                <option value="">Wybierz bundle</option>
                {bundles.map(bundle => (
                  <option key={bundle.id} value={bundle.id}>
                    {bundle.name} ({PRODUCT_FAQ_BUNDLE_STATUS_LABELS[bundle.status]})
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label htmlFor="product-faq-bundle-currency" className="mb-1 block text-xs text-text-secondary">Waluta</label>
              <input
                id="product-faq-bundle-currency"
                type="text"
                maxLength={10}
                value={bundleCurrency}
                onChange={(event) => onChange(current => ({ ...current, currency: event.target.value }))}
                className="input w-full"
              />
            </div>
          </div>

          {selectedBundle && (
            <div className={`rounded-lg border p-3 ${
              selectedBundle.status === 'ACTIVE'
                ? 'border-success-border bg-success-bg/30'
                : 'border-warning/30 bg-warning/5'
            }`}>
              <p className={`text-xs font-medium ${selectedBundle.status === 'ACTIVE' ? 'text-success' : 'text-warning'}`}>
                {selectedBundle.status === 'ACTIVE'
                  ? `Aktywny bundle - ${selectedBundle.packages.length} wariantów cenowych`
                  : 'Ten bundle nie jest aktywny, więc AI nie będzie podawać jego cen'}
              </p>
              {selectedBundle.packages.length > 0 && (
                <div className="mt-2 space-y-1">
                  {selectedBundle.packages.map(pricePackage => (
                    <div key={pricePackage.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-surface-raised/50 px-2.5 py-2 text-xs">
                      <span className="text-text-secondary">{pricePackage.title} · {pricePackage.qty} szt.</span>
                      <span className="font-medium text-text-primary">
                        {pricePackage.promoPrice !== null ? `${pricePackage.promoPrice} ${bundleCurrency}` : `${pricePackage.price} ${bundleCurrency}`}
                        {pricePackage.promoPrice !== null && <span className="ml-1.5 text-text-muted line-through">{pricePackage.price} {bundleCurrency}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {priceVariants.map((variant) => (
            <div key={variant.id} className="space-y-2 rounded-lg border border-border-subtle bg-surface-raised/30 p-3">
              <div className="flex items-start gap-2">
                <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-[1fr_90px]">
                  <div>
                    <label htmlFor={`price-name-${variant.id}`} className="mb-1 block text-xxs text-text-muted">Nazwa wariantu</label>
                    <input
                      id={`price-name-${variant.id}`}
                      type="text"
                      value={variant.name}
                      onChange={(event) => updateVariant(variant.id, { name: event.target.value })}
                      className="input w-full text-xs"
                      placeholder="np. Pakiet 3 sztuki"
                    />
                  </div>
                  <div>
                    <label htmlFor={`price-qty-${variant.id}`} className="mb-1 block text-xxs text-text-muted">Ilość</label>
                    <input
                      id={`price-qty-${variant.id}`}
                      type="number"
                      min={1}
                      step={1}
                      value={variant.quantity}
                      onChange={(event) => updateVariant(variant.id, { quantity: Number(event.target.value) })}
                      className="input w-full text-xs"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onChange(current => ({
                    ...current,
                    priceVariants: priceVariants.filter(item => item.id !== variant.id),
                  }))}
                  className="mt-5 rounded-md p-1.5 text-text-muted hover:bg-danger/10 hover:text-danger"
                  aria-label={`Usuń wariant ${variant.name}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div>
                  <label htmlFor={`price-regular-${variant.id}`} className="mb-1 block text-xxs text-text-muted">Cena regularna</label>
                  <input
                    id={`price-regular-${variant.id}`}
                    type="number"
                    min={0}
                    step="0.01"
                    value={variant.regularPrice ?? ''}
                    onChange={(event) => updateVariant(variant.id, {
                      regularPrice: event.target.value ? Number(event.target.value) : undefined,
                    })}
                    className="input w-full text-xs"
                  />
                </div>
                <div>
                  <label htmlFor={`price-sale-${variant.id}`} className="mb-1 block text-xxs text-text-muted">Cena promocyjna</label>
                  <input
                    id={`price-sale-${variant.id}`}
                    type="number"
                    min={0}
                    step="0.01"
                    value={variant.salePrice ?? ''}
                    onChange={(event) => updateVariant(variant.id, {
                      salePrice: event.target.value ? Number(event.target.value) : undefined,
                    })}
                    className="input w-full text-xs"
                  />
                </div>
                <div>
                  <label htmlFor={`price-currency-${variant.id}`} className="mb-1 block text-xxs text-text-muted">Waluta</label>
                  <input
                    id={`price-currency-${variant.id}`}
                    type="text"
                    maxLength={10}
                    value={variant.currency}
                    onChange={(event) => updateVariant(variant.id, { currency: event.target.value })}
                    className="input w-full text-xs"
                  />
                </div>
              </div>

              <div>
                <label htmlFor={`price-note-${variant.id}`} className="mb-1 block text-xxs text-text-muted">Bonusy lub uwagi</label>
                <input
                  id={`price-note-${variant.id}`}
                  type="text"
                  value={variant.note || ''}
                  onChange={(event) => updateVariant(variant.id, { note: event.target.value })}
                  className="input w-full text-xs"
                  placeholder="np. darmowa wysyłka i e-book"
                />
              </div>
            </div>
          ))}

          <button type="button" onClick={addPriceVariant} className="btn-secondary flex items-center gap-1.5 text-xs">
            <Plus className="h-3.5 w-3.5" /> Dodaj wariant cenowy
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// Product FAQ base product selector
// ============================================

function ProductFaqProductSelect({
  value,
  products,
  loading,
  onChange,
}: {
  value: string;
  products: SalesProductOption[];
  loading: boolean;
  onChange: (productId: string) => void;
}) {
  return (
    <div>
      <label htmlFor="product-faq-base-product" className="text-xs text-text-secondary mb-1 block">Produkt bazowy</label>
      <Select
        id="product-faq-base-product"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={loading}
        placeholder={loading ? 'Ładowanie produktów...' : 'Wybierz produkt z logistyki'}
      >
        <option value="" disabled>Wybierz produkt z logistyki</option>
        {products.map(product => (
          <option key={product.id} value={product.id}>
            {product.name}{product.sku ? ` (${product.sku})` : ''}
          </option>
        ))}
      </Select>
      <p className="text-xxs text-text-disabled mt-1">
        Lista pochodzi z modułu logistyki i obejmuje produkty powiązane z WooCommerce.
      </p>
    </div>
  );
}

// ============================================
// SelectField component (with "Inne..." custom option)
// ============================================

function SelectField({ field, value, onChange, hideLabel }: {
  field: SectionFieldDef;
  value: unknown;
  onChange: (val: unknown) => void;
  hideLabel?: boolean;
}) {
  const strValue = value !== undefined && value !== null ? String(value) : '';
  const options = field.options || [];
  const isPresetValue = !strValue || options.some(o => o.value === strValue);
  const [isCustom, setIsCustom] = useState(!isPresetValue);

  // Sync custom mode if value changes externally
  useEffect(() => {
    if (strValue && !options.some(o => o.value === strValue)) {
      setIsCustom(true);
    }
  }, [strValue, options]);

  if (isCustom && field.allowCustom) {
    return (
      <div>
        {!hideLabel && <label className="text-xs text-text-secondary mb-1 block">{field.label}</label>}
        <div className="flex gap-2">
          <input
            type="text"
            value={strValue}
            onChange={(e) => onChange(e.target.value)}
            className="input flex-1"
            placeholder="Wpisz wartość..."
          />
          <button
            onClick={() => { setIsCustom(false); onChange(''); }}
            className="px-2.5 py-1.5 text-xs text-text-secondary hover:text-accent-primary border border-border rounded-lg hover:border-accent-primary/30 transition-all whitespace-nowrap"
          >
            Z listy
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {!hideLabel && <label className="text-xs text-text-secondary mb-1 block">{field.label}</label>}
      <Select
        value={strValue}
        onChange={(e) => {
          if (e.target.value === '__custom__') {
            setIsCustom(true);
            onChange('');
          } else {
            // Convert to number if all options are numeric
            const allNumeric = options.every(o => !isNaN(Number(o.value)));
            onChange(allNumeric && e.target.value ? Number(e.target.value) : e.target.value);
          }
        }}
        className="input w-full"
      >
        <option value="">Wybierz...</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
        {field.allowCustom && (
          <option value="__custom__">Inne...</option>
        )}
      </Select>
    </div>
  );
}

// ============================================
// MultiCheckboxField component
// ============================================

function MultiCheckboxField({ field, value, onChange, hideLabel }: {
  field: SectionFieldDef;
  value: unknown;
  onChange: (val: unknown) => void;
  hideLabel?: boolean;
}) {
  const selected = Array.isArray(value) ? value as string[] : [];
  const options = field.options || [];
  const presetValues = options.map(o => o.value);
  const customValues = selected.filter(v => !presetValues.includes(v));
  const [addingCustom, setAddingCustom] = useState(false);
  const [customInput, setCustomInput] = useState('');

  const toggle = (val: string) => {
    if (selected.includes(val)) {
      onChange(selected.filter(v => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const addCustom = () => {
    const trimmed = customInput.trim();
    if (trimmed && !selected.includes(trimmed)) {
      onChange([...selected, trimmed]);
    }
    setCustomInput('');
    setAddingCustom(false);
  };

  return (
    <div>
      {!hideLabel && <label className="text-xs text-text-secondary mb-2 block">{field.label}</label>}
      <div className="grid grid-cols-2 gap-1.5">
        {options.map(o => {
          const isChecked = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => toggle(o.value)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-left transition-all border ${
                isChecked
                  ? 'bg-accent-primary/10 border-accent-primary/30 text-accent-primary'
                  : 'bg-surface-base/30 border-border-subtle text-text-secondary hover:border-border hover:text-text-secondary'
              }`}
            >
              <div className={`w-3.5 h-3.5 rounded flex-shrink-0 flex items-center justify-center border transition-all ${
                isChecked ? 'bg-accent-primary/20 border-accent-primary/50' : 'border-border-strong'
              }`}>
                {isChecked && <Check className="w-2.5 h-2.5" />}
              </div>
              {o.label}
            </button>
          );
        })}
      </div>

      {/* Custom values */}
      {customValues.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {customValues.map(v => (
            <span key={v} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-accent-primary/10 border border-accent-primary/20 text-xs text-accent-primary">
              {v}
              <button onClick={() => toggle(v)} className="hover:text-text-primary transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add custom */}
      {field.allowCustom && (
        addingCustom ? (
          <div className="flex gap-2 mt-2">
            <input
              type="text"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustom()}
              className="input flex-1 text-xs"
              placeholder="Wpisz własną metodę..."
              autoFocus
            />
            <button onClick={addCustom} className="px-2.5 py-1.5 text-xs text-accent-primary hover:bg-accent-primary/10 rounded-lg transition-colors">
              Dodaj
            </button>
            <button onClick={() => { setAddingCustom(false); setCustomInput(''); }} className="px-2 py-1.5 text-xs text-text-muted hover:text-text-primary transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setAddingCustom(true)}
            className="mt-2 text-xs text-text-muted hover:text-accent-primary flex items-center gap-1 transition-colors"
          >
            <Plus className="w-3 h-3" /> Dodaj własną
          </button>
        )
      )}
    </div>
  );
}

// ============================================
// InlineSelect helper for carrier fields
// ============================================

function InlineSelect({ value, onChange, options, placeholder, className }: {
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  className?: string;
}) {
  const isPreset = !value || options.some(o => o.value === value);
  const [isCustom, setIsCustom] = useState(!isPreset);

  useEffect(() => {
    if (value && !options.some(o => o.value === value)) {
      setIsCustom(true);
    }
  }, [value, options]);

  if (isCustom) {
    return (
      <div className={`flex gap-1 ${className || ''}`}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input flex-1 min-w-0"
          placeholder={placeholder}
        />
        <button
          onClick={() => { setIsCustom(false); onChange(''); }}
          className="p-1.5 text-text-muted hover:text-accent-primary transition-colors flex-shrink-0"
          title="Wybierz z listy"
        >
          <ChevronDown className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <Select
      value={value}
      onChange={(e) => {
        if (e.target.value === '__custom__') {
          setIsCustom(true);
          onChange('');
        } else {
          onChange(e.target.value);
        }
      }}
      className={`input ${className || ''}`}
    >
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
      <option value="__custom__">Inne...</option>
    </Select>
  );
}

// ============================================
// SectionField component
// ============================================

function SectionField({ field, value, onChange, hideLabel }: {
  field: SectionFieldDef;
  value: unknown;
  onChange: (val: unknown) => void;
  hideLabel?: boolean;
}) {
  if (field.type === 'select') {
    return <SelectField field={field} value={value} onChange={onChange} hideLabel={hideLabel} />;
  }

  if (field.type === 'multi_checkbox') {
    return <MultiCheckboxField field={field} value={value} onChange={onChange} hideLabel={hideLabel} />;
  }

  if (field.type === 'text') {
    return (
      <div>
        {!hideLabel && <label className="text-xs text-text-secondary mb-1 block">{field.label}</label>}
        <input
          type="text"
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          className="input w-full"
          placeholder={field.placeholder}
        />
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <div>
        {!hideLabel && <label className="text-xs text-text-secondary mb-1 block">{field.label}</label>}
        <input
          type="number"
          value={value !== undefined && value !== null ? String(value) : ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
          className="input w-full"
          placeholder={field.placeholder}
        />
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div>
        {!hideLabel && <label className="text-xs text-text-secondary mb-1 block">{field.label}</label>}
        <textarea
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          className="input w-full min-h-[80px] resize-y"
          placeholder={field.placeholder}
          rows={3}
        />
      </div>
    );
  }

  if (field.type === 'string_list') {
    const items = Array.isArray(value) ? value as string[] : [];
    return (
      <div>
        <label className="text-xs text-text-secondary mb-1 block">{field.label}</label>
        <input
          type="text"
          value={items.join(', ')}
          onChange={(e) => onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
          className="input w-full"
          placeholder={field.placeholder}
        />
        <p className="text-xxs text-text-disabled mt-0.5">Oddziel przecinkami</p>
      </div>
    );
  }

  if (field.type === 'repeatable_carriers') {
    const carriers = Array.isArray(value) ? value as { name: string; cost: string; deliveryTime: string }[] : [];
    return (
      <div>
        <label className="text-xs text-text-secondary mb-2 block">{field.label}</label>
        <div className="space-y-2">
          {carriers.map((carrier, idx) => (
            <div key={idx} className="flex gap-2 items-start">
              <InlineSelect
                value={carrier.name}
                onChange={(val) => {
                  const updated = [...carriers];
                  updated[idx] = { ...carrier, name: val };
                  onChange(updated);
                }}
                options={CARRIER_NAME_OPTIONS}
                placeholder="Przewoźnik"
                className="flex-1"
              />
              <input
                type="text"
                value={carrier.cost}
                onChange={(e) => {
                  const updated = [...carriers];
                  updated[idx] = { ...carrier, cost: e.target.value };
                  onChange(updated);
                }}
                className="input w-24"
                placeholder="Koszt"
              />
              <InlineSelect
                value={carrier.deliveryTime}
                onChange={(val) => {
                  const updated = [...carriers];
                  updated[idx] = { ...carrier, deliveryTime: val };
                  onChange(updated);
                }}
                options={DELIVERY_TIME_OPTIONS}
                placeholder="Czas"
                className="w-40"
              />
              <button
                onClick={() => onChange(carriers.filter((_, i) => i !== idx))}
                className="p-2 text-text-muted hover:text-danger transition-colors flex-shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => onChange([...carriers, { name: '', cost: '', deliveryTime: '' }])}
          className="mt-2 text-xs text-text-muted hover:text-accent-primary flex items-center gap-1 transition-colors"
        >
          <Plus className="w-3 h-3" /> Dodaj przewoźnika
        </button>
      </div>
    );
  }

  if (field.type === 'repeatable_faq') {
    const faqs = Array.isArray(value) ? value as { question: string; answer: string }[] : [];
    return (
      <div>
        <label className="text-xs text-text-secondary mb-2 block">{field.label}</label>
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div key={idx} className="p-3 rounded-lg bg-surface-base/30 border border-border-subtle space-y-2">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    value={faq.question}
                    onChange={(e) => {
                      const updated = [...faqs];
                      updated[idx] = { ...faq, question: e.target.value };
                      onChange(updated);
                    }}
                    className="input w-full text-sm"
                    placeholder="Pytanie"
                  />
                </div>
                <button
                  onClick={() => onChange(faqs.filter((_, i) => i !== idx))}
                  className="p-1.5 text-text-muted hover:text-danger transition-colors flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <textarea
                value={faq.answer}
                onChange={(e) => {
                  const updated = [...faqs];
                  updated[idx] = { ...faq, answer: e.target.value };
                  onChange(updated);
                }}
                className="input w-full text-sm min-h-[50px] resize-y"
                placeholder="Odpowiedź"
                rows={2}
              />
            </div>
          ))}
        </div>
        <button
          onClick={() => onChange([...faqs, { question: '', answer: '' }])}
          className="mt-2 text-xs text-text-muted hover:text-accent-primary flex items-center gap-1 transition-colors"
        >
          <Plus className="w-3 h-3" /> Dodaj pytanie
        </button>
      </div>
    );
  }

  return null;
}
